import { useState } from "react";
import { Link } from "wouter";
import { useGetMe, getGetMeQueryKey } from "@workspace/api-client-react";
import { useClerk, Show } from "@clerk/react";
import { Button } from "@workspace/ui-web/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@workspace/ui-web/components/ui/sheet";
import { Loader2, Menu } from "lucide-react";

const publicLinks = [
  { href: "/about", label: "About" },
  { href: "/ways-to-give", label: "Ways to Give" },
  { href: "/apply", label: "Apply to Raise" },
  { href: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { data: user, isLoading } = useGetMe({
    query: { retry: false, queryKey: getGetMeQueryKey() },
  });
  const { signOut } = useClerk();
  const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between px-4 md:px-8">
        <Link href="/" className="flex items-center gap-2">
          <img
            src={`${basePath}/logo.jpeg`}
            alt="Greater Hope Service Dogs"
            className="h-10 w-10 rounded-full object-contain"
          />
          <span className="hidden sm:inline-block font-bold text-lg text-primary tracking-tight">
            Greater Hope Service Dogs
          </span>
        </Link>

        {/* Desktop public navigation */}
        <nav className="hidden md:flex items-center gap-6">
          {publicLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Desktop auth actions */}
        <div className="hidden md:flex items-center gap-4">
          <Show when="signed-out">
            <Link
              href="/sign-in"
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Sign In
            </Link>
            <Link href="/sign-up">
              <Button>Become a Raiser</Button>
            </Link>
          </Show>

          <Show when="signed-in">
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : (
              <>
                <Link
                  href="/portal"
                  className="text-sm font-medium hover:text-primary transition-colors"
                >
                  My Portal
                </Link>
                {user?.role === "admin" && (
                  <Link
                    href="/admin"
                    className="text-sm font-medium text-destructive hover:text-destructive/80 transition-colors"
                  >
                    Admin Dashboard
                  </Link>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => signOut({ redirectUrl: basePath || "/" })}
                >
                  Log out
                </Button>
              </>
            )}
          </Show>
        </div>

        {/* Mobile menu */}
        <div className="md:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <SheetTitle className="text-primary">
                Greater Hope Service Dogs
              </SheetTitle>
              <nav className="mt-8 flex flex-col gap-1">
                {publicLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="py-2 text-base font-medium text-foreground hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}

                <div className="my-4 border-t" />

                <Show when="signed-out">
                  <Link
                    href="/sign-in"
                    onClick={() => setMobileOpen(false)}
                    className="py-2 text-base font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Sign In
                  </Link>
                  <Link href="/sign-up" onClick={() => setMobileOpen(false)}>
                    <Button className="w-full mt-2">Become a Raiser</Button>
                  </Link>
                </Show>

                <Show when="signed-in">
                  <Link
                    href="/portal"
                    onClick={() => setMobileOpen(false)}
                    className="py-2 text-base font-medium text-foreground hover:text-primary transition-colors"
                  >
                    My Portal
                  </Link>
                  {user?.role === "admin" && (
                    <Link
                      href="/admin"
                      onClick={() => setMobileOpen(false)}
                      className="py-2 text-base font-medium text-destructive hover:text-destructive/80 transition-colors"
                    >
                      Admin Dashboard
                    </Link>
                  )}
                  <Button
                    variant="outline"
                    className="w-full mt-2"
                    onClick={() => {
                      setMobileOpen(false);
                      signOut({ redirectUrl: basePath || "/" });
                    }}
                  >
                    Log out
                  </Button>
                </Show>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
